import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bot, CheckCircle2, ChevronDown, FileSignature, Flag, Keyboard, Lock, Play, Sparkles, type LucideIcon } from "lucide-react";
import { useApi, useInvalidateOn, type AiProposal, type Citation, type WorkflowInstanceView } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, InlineAlert, MarkdownBody, Popover, PopoverContent, PopoverTrigger, RiskGauge, Skeleton, SourceDrawer, SourceText, Tooltip, cn, toast, useSave } from "@ncp/ui";
import { riskRationaleText } from "@/lib/risk-rationale";
import { AgentRunPanel } from "@ncp/assistant-sdk";
import type { CaseDetail } from "../api";
import { ResolveChecklistItemDialog } from "../ResolveChecklistItemDialog";
import { ConfidenceMeter, CriterionCard } from "./CriterionCard";
import { Kbd, submitKeys, verdictMeta } from "./ui";
import { ConcludeSheet } from "./ConcludeSheet";
import { IssuesPanel, RaiseIssueDialog } from "./IssuesPanel";
import { FigureInfo } from "../MethodInfo";
import { blockerText, sameDirection, type FindingVerdict, type ReviewSource, type ReviewView } from "./api";

interface AgentInfo { key: string; name: string; nameAr: string; tier: string }

/** Height of the sticky app header plus breathing room; the card is brought no higher than this when it has to be scrolled to. */
const HEADER_OFFSET = 84;

export interface ReviewTabProps {
  detail: CaseDetail;
  workflow?: WorkflowInstanceView;
  proposals: AiProposal[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  /** Opens another surface of the workspace (file:documents, analysis:report, decision, history:process …) with an optional record id. */
  onOpenSurface: (surface: string, id?: string) => void;
  /** A next step the officer chose from the header or the Command Center: the conclusion opens with it selected. */
  pendingStep?: string | null;
  onStepHandled?: () => void;
  /** The criterion that holds the screen's one primary action (the next one to decide), when it is the one open. */
  primaryCriterion?: string | null;
}

/**
 * The stage review as a workbench: the criteria of the stage down the side with where each one stands, the criterion under review in
 * the middle with the law, the platform's view and the officer's finding, and the stage's issues and the AI case summary folded away
 * until needed. The pass is keyed like a checklist (1–4 decide, A accepts the AI, ←/→ move, I raises an
 * issue) so a clean file is a short run of keystrokes and the officer stops only where they disagree. Moving between criteria never
 * moves the page unless the card has left the viewport. The way out is the conclusion, never a bare transition.
 */
export function ReviewTab({ detail, workflow, proposals, selectedKey, onSelect, onOpenSurface, pendingStep, onStepHandled, primaryCriterion }: ReviewTabProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const ar = locale === "ar";
  const c = detail.summary;
  useInvalidateOn(["review.updated", "case.updated", "case.transitioned", "proposal.created", "proposal.updated", "agent.run"], [["case-review", c.id]]);
  const review = useQuery({ queryKey: ["case-review", c.id, locale], queryFn: () => api.get<ReviewView>(`/api/cases/${c.id}/review`, { locale }) });
  const agents = useQuery({ queryKey: ["agents"], queryFn: () => api.get<AgentInfo[]>("/api/ai/agents") });
  const agentName = useCallback((key: string) => { const a = agents.data?.find((x) => x.key === key); return a ? (ar ? a.nameAr : a.name) : key; }, [agents.data, ar]);
  const runAgent = useSave({ mutationFn: (agentKey: string) => api.post(`/api/ai/cases/${c.id}/run`, { agentKey }), onSuccess: (_, k) => toast.info(t("backoffice:workspace.runAgent"), { description: agentName(k) }) });

  const [citation, setCitation] = useState<Citation | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [raising, setRaising] = useState(false);
  const [concluding, setConcluding] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "awaiting" | "disagree">("all");
  const hotkeys = useRef<{ verdict: (v: Exclude<FindingVerdict, "Pending">) => void; acceptAi: () => void; focusNote: () => void } | null>(null);

  // The review of the stage the file has just left stays in the cache until its refresh arrives; it is not shown as the current one.
  const data = review.data?.stageKey === c.state ? review.data : undefined;
  const criteria = data?.criteria ?? [];
  const [stepTrigger, setStepTrigger] = useState<string | null>(null);
  const [issueRequest, setIssueRequest] = useState<string | null>(null);
  const clearIssueRequest = useCallback(() => setIssueRequest(null), []);
  useEffect(() => {
    if (!pendingStep || !data) return;
    if (pendingStep.startsWith("issue:")) setIssueRequest(pendingStep.slice("issue:".length));
    else { setStepTrigger(pendingStep); setConcluding(true); }
    onStepHandled?.();
  }, [pendingStep, data, onStepHandled]);
  const readOnly = !data || data.status !== "Open";
  // The selection is the URL's criterion when it exists, else the first criterion still waiting on the officer, else the first.
  const index = useMemo(() => {
    if (criteria.length === 0) return -1;
    const fromUrl = selectedKey ? criteria.findIndex((x) => x.key === selectedKey) : -1;
    if (fromUrl >= 0) return fromUrl;
    const next = data?.checklist?.primary.kind === "criterion" ? criteria.findIndex((x) => `criterion:${x.key}` === data.checklist!.primary.target) : -1;
    if (next >= 0) return next;
    const pending = criteria.findIndex((x) => !x.finding || x.finding.verdict === "Pending");
    return pending >= 0 ? pending : 0;
  }, [criteria, selectedKey, data?.checklist]);
  const current = index >= 0 ? criteria[index] : undefined;
  const go = useCallback((next: number) => { if (criteria.length === 0) return; const i = Math.max(0, Math.min(criteria.length - 1, next)); onSelect(criteria[i].key); }, [criteria, onSelect]);
  // Moving to another criterion leaves the page where it is, unless the card's top has slipped behind the header or (all but) below
  // the fold; then it is brought back in one step, no animation, so the eye never has to follow a scroll.
  const cardRef = useRef<HTMLDivElement>(null);
  const lastKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!current || lastKey.current === current.key) return;
    const first = lastKey.current === undefined;
    lastKey.current = current.key;
    if (first) return;
    const el = cardRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top < HEADER_OFFSET - 24 || top > window.innerHeight - 160) window.scrollTo({ top: window.scrollY + top - HEADER_OFFSET, behavior: "instant" });
  }, [current]);

  // Keyboard: the whole pass without the mouse. Ignored while typing.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT" || target.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector("[role=dialog]")) return;
      // Standard keys only (visual items 4 and 27): the arrows move between criteria; letters and digits do nothing, so no single key records,
      // opens the platform's assessment or concludes the stage.
      if (e.key === "ArrowRight") { e.preventDefault(); go(ar ? index - 1 : index + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(ar ? index + 1 : index - 1); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go, index, readOnly, ar, data?.progress.canConclude]);

  const openSource = (s: ReviewSource) => {
    switch (s.kind) {
      case "Document": onOpenSurface("file:documents", s.id); break;
      case "Letter": case "Rfi": onOpenSurface("file:correspondence", s.id); break;
      case "Party": onOpenSurface("file:parties"); break;
      case "Decision": onOpenSurface("decision", s.id); break;
      case "Clock": case "Task": onOpenSurface("history:process"); break;
      case "Meeting": onOpenSurface(c.type === "EconomicConcentration" ? "history:process" : "analysis:investigation"); break;
      case "Publication": case "Objection": onOpenSurface("analysis:publication", s.id); break;
      case "Proposal": { const p = proposals.find((x) => x.id === s.id); if (p) setRunId(p.runId); break; }
      case "Citation": setCitation({ id: s.id, sourceType: "Regulation", sourceId: s.id, title: s.title, locator: s.locator ?? null, excerpt: null, supported: s.status === "Unsupported" ? false : null }); break;
      case "Case": if (s.id !== c.id) window.open(`/cases/${s.id}`, "_blank", "noopener"); break;
      case "Tool": if (s.id === "00000000-0000-0000-0000-000000000000") break; onOpenSurface("analysis"); break;
      default: onOpenSurface("analysis"); break;
    }
  };

  if (review.isLoading || !data) return <div className="@container"><div className="grid gap-6 @4xl:grid-cols-[300px_minmax(0,1fr)]"><Skeleton className="h-96" /><Skeleton className="h-[32rem]" /></div></div>;

  const summary = ar && detail.summaryAr ? detail.summaryAr : detail.summary2;
  const stageLabel = ar ? data.stageLabelAr : data.stageLabel;
  const openIssues = data.issues.filter((i) => i.status === "Open");
  const visible = criteria.map((x, i) => ({ x, i })).filter(({ x }) => {
    const f = x.finding && x.finding.verdict !== "Pending" ? x.finding.verdict : null;
    if (filter === "awaiting") return !f;
    if (filter === "disagree") return !!f && !!x.ai && x.ai.verdict !== "Unknown" && !sameDirection(x.ai.verdict, f);
    return true;
  });

  return (
    <div className="@container space-y-4">
      {readOnly ? <InlineAlert tone="info" title={t("backoffice:review.readOnlyTitle")} /> : null}
      {/* The list sits beside the criterion whenever the work area itself is wide enough, whatever the window or the sidebar. */}
      <div className="grid items-start gap-6 @4xl:grid-cols-[300px_minmax(0,1fr)]">
        {/* ------------------------------------------------------------ the criteria, then the stage's context — sticky so the list never scrolls away */}
        <aside className="space-y-3 @4xl:sticky @4xl:top-20 @4xl:order-1 @4xl:max-h-[calc(100dvh-6rem)] @4xl:overflow-y-auto @4xl:pe-1 scrollbar-thin" tabIndex={-1}>
          <Card className="overflow-hidden border-aegold-200">
            <div className="flex items-center gap-3 px-4 py-3">
              <ProgressRing value={data.progress.decided} total={data.progress.total} />
              <p className="min-w-0 flex-1 text-sm font-semibold">{t("backoffice:review.decidedOf", { done: data.progress.decided, total: data.progress.total })}{data.visit > 1 ? <span className="font-normal text-muted-foreground"> · {t("backoffice:review.visit", { n: data.visit })}</span> : null}</p>
              {!readOnly && criteria.length > 0 ? <HotkeyHelp canConclude={data.progress.canConclude} /> : null}
              {!readOnly ? <RunAgentMenu agents={agents.data ?? []} stageHooks={data.stageAgentHooks} onRun={(k) => runAgent.mutate(k)} /> : null}
            </div>
            {criteria.length > 8 ? (
              <div className="flex items-center gap-1 border-t border-sand-200 px-3 py-2" role="tablist" aria-label={t("backoffice:review.filter.label")}>
                {(["all", "awaiting", "disagree"] as const).map((f) => {
                  const count = f === "all" ? criteria.length : f === "awaiting" ? criteria.filter((x) => !x.finding || x.finding.verdict === "Pending").length : criteria.filter((x) => x.finding && x.finding.verdict !== "Pending" && x.ai && x.ai.verdict !== "Unknown" && !sameDirection(x.ai.verdict, x.finding.verdict)).length;
                  return <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} className={cn("rounded-full border px-2 py-0.5 text-[11px] transition-colors", filter === f ? "border-aegold-400 bg-aegold-50 font-semibold text-aegold-900" : "border-sand-200 text-sand-600 hover:bg-sand-50")}>{t(`backoffice:review.filter.${f}`)} <span className="tabular">{count}</span></button>;
                })}
              </div>
            ) : null}
            <ol className="border-t border-sand-200" aria-label={t("backoffice:review.criteria")}>
              {visible.map(({ x, i }) => {
                const f = x.finding && x.finding.verdict !== "Pending" ? x.finding.verdict : null;
                const meta = verdictMeta[f ?? "Pending"];
                const Icon = meta.icon;
                const disagree = !!f && !!x.ai && x.ai.verdict !== "Unknown" && !sameDirection(x.ai.verdict, f);
                const active = i === index;
                const status = f ? t(`backoffice:review.verdicts.${f}`) : x.required ? t("backoffice:review.undecided") : t("backoffice:review.optional");
                return (
                  <li key={x.key}>
                    <button type="button" onClick={() => onSelect(x.key)} aria-current={active ? "true" : undefined} title={status} className={cn("flex w-full items-start gap-2.5 border-s-[3px] px-3 py-2 text-start transition-colors hover:bg-sand-50", active ? "border-aegold-600 bg-aegold-50/70" : "border-transparent")}>
                      <Icon className={cn("mt-0.5 size-4 shrink-0", f ? meta.tone : "text-sand-300")} aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className={cn("block text-sm leading-snug text-aeblack-900", active && "font-semibold")}>{ar ? x.titleAr : x.title}<span className="sr-only"> · {status}</span></span>
                        {x.ai && x.ai.verdict !== "Unknown" ? (
                          <span className="mt-1 flex items-center gap-2 text-[11px] text-seablue-900">
                            <span className="truncate">{t(`backoffice:review.verdicts.${x.ai.verdict}`)}</span>
                            <ConfidenceMeter value={x.ai.confidence} compact />
                          </span>
                        ) : null}
                      </span>
                      {!f && !x.required ? <span className="mt-0.5 shrink-0 text-[11px] text-muted-foreground">{t("backoffice:review.optional")}</span> : null}
                      {disagree ? <Tooltip content={t("backoffice:review.filter.disagree")}><span className="shrink-0 font-semibold text-warning-700">≠</span></Tooltip> : null}
                    </button>
                  </li>
                );
              })}
              {visible.length === 0 ? <li className="px-3 py-4 text-center text-xs text-muted-foreground">{t("backoffice:review.filter.empty")}</li> : null}
            </ol>
            {/* One key for the marks beside the criteria (visual item 22): each mark is the officer's verdict, a grey ring is not decided yet. */}
            {criteria.length > 0 ? <CriteriaKey verdicts={[...new Set(criteria.map((x) => (x.finding && x.finding.verdict !== "Pending" ? x.finding.verdict : "Pending") as FindingVerdict))]} /> : null}
            {criteria.length > 0 ? <div className="border-t border-sand-200 p-3">
              {readOnly ? (
                <Badge variant={data.status === "Concluded" ? "success" : "outline"} className="w-full justify-center"><CheckCircle2 /> {t(`backoffice:review.status.${data.status}`)}</Badge>
              ) : (
                <Tooltip content={data.progress.canConclude ? t("backoffice:review.concludeReady") : data.progress.blockers.map((b) => blockerText(b, t)).join(" · ")}>
                  <Button variant={data.progress.canConclude ? "gold" : "outline"} className="w-full justify-between" onClick={() => setConcluding(true)}>
                    <span className="flex items-center gap-2">{data.progress.canConclude ? <FileSignature /> : <Lock />} {t("backoffice:review.concludeStage")}</span>
                    {/* The items still needed to conclude – the ones tagged "Needed" in the stage's list (7.4). */}
                    {(data.checklist?.needed ?? 0) > 0 ? <span className="text-xs">{t("backoffice:r3Process.left.needed", { count: data.checklist!.needed })}</span> : null}
                  </Button>
                </Tooltip>
              )}
            </div> : null}
          </Card>

          {data.issues.length > 0 ? (
            <ContextSection id="stage-issues" icon={Flag} title={t("backoffice:review.issues")} count={openIssues.length} badge={openIssues.some((i) => i.severity === "Blocking") ? "danger" : openIssues.length ? "warning" : "outline"} defaultOpen={openIssues.length > 0}>
              <IssuesPanel caseId={c.id} issues={data.issues} criteria={criteria} readOnly={readOnly} onSelectCriterion={(k) => onSelect(k)} onRaise={() => setRaising(true)} embedded resolveRequest={issueRequest} onResolveHandled={clearIssueRequest} />
            </ContextSection>
          ) : null}

          {summary || c.riskScore != null ? (
            <ContextSection icon={Sparkles} title={t("backoffice:workspace.aiSummary")}>
              <div className="flex items-start gap-3">
                {c.riskScore != null ? <RiskGauge score={c.riskScore} size={84} label={<span className="inline-flex items-center gap-0.5">{t("misc.riskScore")}<FigureInfo measure={c.type === "EconomicConcentration" ? "riskScore" : "fileRiskScore"} /></span>} /> : null}
                <div className="min-w-0 flex-1" {...(ar && summary && !/[\u0600-\u06FF]/.test(summary) ? { lang: "en", dir: "ltr" } : {})}>{summary ? <MarkdownBody className="prose-sm text-xs">{summary}</MarkdownBody> : <p className="text-xs text-muted-foreground">{t("backoffice:workspace.noSummary")}</p>}</div>
              </div>
              {detail.riskRationale ? <p className="mt-2 text-[11px] text-sand-700">{riskRationaleText(detail.riskRationale, locale) ?? <SourceText text={detail.riskRationale} />}</p> : null}
            </ContextSection>
          ) : null}
        </aside>

        {/* ------------------------------------------------------------ the criterion under review; a minimum height so the page never shortens under the reader */}
        <div ref={cardRef} id="criterion-card" className="min-w-0 space-y-4 scroll-mt-24 @4xl:order-2 @4xl:min-h-[70dvh]">
          {criteria.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle2 className="mx-auto size-8 text-success-600" />
              <p className="mt-2 text-base font-semibold">{t("backoffice:review.terminalTitle", { stage: stageLabel })}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t("backoffice:review.terminalHint")}</p>
              <Button variant="outline" className="mt-4" onClick={() => onOpenSurface("history:reviews")}>{t("backoffice:review.stageReviews")}</Button>
            </Card>
          ) : null}
          {current ? (
            <CriterionCard caseId={c.id} criterion={current} index={index} total={criteria.length} readOnly={readOnly} proposals={proposals} agentName={agentName} primary={!!primaryCriterion && primaryCriterion === current.key} officerFirst={review.data?.officerViewFirst ?? true}
              onPrev={() => go(index - 1)} onNext={() => go(index + 1)} onOpenSource={openSource} onOpenSurface={(s) => onOpenSurface(s)} onRaiseIssue={() => setRaising(true)} onOpenRun={setRunId}
              onResolveChecklistItem={setResolving} registerHotkeys={(h) => { hotkeys.current = h; }} />
          ) : null}
          {runId ? <AgentRunPanel runId={runId} onClose={() => setRunId(null)} compact /> : null}
        </div>
      </div>

      <SourceDrawer citation={citation} onClose={() => setCitation(null)} />
      <RaiseIssueDialog caseId={c.id} open={raising} onOpenChange={setRaising} criteria={criteria} defaultCriterionKey={current?.key} />
      <ConcludeSheet caseId={c.id} review={data} workflow={workflow} open={concluding} initialTrigger={data.transitions.some((tr) => tr.trigger === stepTrigger) ? stepTrigger : data.checklist?.forwardTrigger} onOpenChange={(o) => { setConcluding(o); if (!o) setStepTrigger(null); }} onConcluded={() => onSelect(null)} />
      <ResolveChecklistItemDialog caseId={c.id} itemKey={resolving} open={resolving !== null} onOpenChange={(o) => { if (!o) setResolving(null); }} />
    </div>
  );
}

/** The key to the marks beside the criteria: only the marks the list uses, each with its word. */
function CriteriaKey({ verdicts }: { verdicts: FindingVerdict[] }) {
  const { t } = useTranslation();
  const order: FindingVerdict[] = ["Satisfied", "Deficient", "NeedsInformation", "NotApplicable", "Pending"];
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-sand-100 px-3 py-2 text-[11px] text-sand-700" aria-label={t("backoffice:r3Process.criterion.key")}>
      {order.filter((v) => verdicts.includes(v)).map((v) => {
        const Icon = verdictMeta[v].icon;
        return <span key={v} className="inline-flex items-center gap-1"><Icon className={cn("size-3.5", v === "Pending" ? "text-sand-300" : verdictMeta[v].tone)} aria-hidden />{v === "Pending" ? t("backoffice:review.undecided") : t(`backoffice:review.verdicts.${v}`)}</span>;
      })}
    </p>
  );
}

/** A block of the rail that opens on demand: one line when closed (icon, title, count), the content when open. */
function ContextSection({ id, icon: Icon, title, count, badge = "outline", defaultOpen = false, children }: { id?: string; icon: LucideIcon; title: string; count?: number; badge?: "outline" | "warning" | "danger" | "ai"; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card id={id} className="overflow-hidden scroll-mt-24">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center gap-2 px-4 py-2.5 text-start hover:bg-sand-50">
        <Icon className="size-3.5 shrink-0 text-aegold-700" />
        <span className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wider text-sand-600">{title}</span>
        {count ? <Badge size="sm" variant={badge}>{count}</Badge> : null}
        <ChevronDown className={cn("size-3.5 shrink-0 text-sand-400 transition-transform", open && "rotate-180")} />
      </button>
      {open ? <div className="border-t border-sand-100 p-4 pt-3">{children}</div> : null}
    </Card>
  );
}

/** The keyboard pass, one popover instead of a paragraph under the list. */
function HotkeyHelp({ canConclude }: { canConclude: boolean }) {
  const { t } = useTranslation();
  void canConclude;
  const rows: Array<[ReactNode, string]> = [
    [<><Kbd>←</Kbd><Kbd>→</Kbd></>, t("backoffice:review.move")],
    [<Kbd>{submitKeys}</Kbd>, t("backoffice:review.hotkeyRecord")],
  ];
  return (
    <Popover>
      <PopoverTrigger asChild><Button size="icon-sm" variant="ghost" className="size-7 shrink-0 text-sand-500" aria-label={t("backoffice:review.hotkeys")}><Keyboard className="size-4" /></Button></PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:review.hotkeys")}</p>
        <ul className="space-y-1.5 text-xs">
          {rows.map(([keys, label], i) => <li key={i} className="flex items-center justify-between gap-3"><span className="text-aeblack-900">{label}</span><span className="flex items-center gap-0.5">{keys}</span></li>)}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** Runs an agent on the case: the stage's own agents first, then every other. */
function RunAgentMenu({ agents, stageHooks, onRun }: { agents: AgentInfo[]; stageHooks: string[]; onRun: (key: string) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const runnable = agents.filter((a) => a.tier !== "n/a");
  const first = runnable.filter((a) => stageHooks.includes(a.key));
  const rest = runnable.filter((a) => !stageHooks.includes(a.key));
  const item = (a: AgentInfo) => <DropdownMenuItem key={a.key} onSelect={() => onRun(a.key)}><Play /> {locale === "ar" ? a.nameAr : a.name}</DropdownMenuItem>;
  return (
    <DropdownMenu>
      <Tooltip content={t("backoffice:workspace.runAgent")}>
        <DropdownMenuTrigger asChild><Button size="icon-sm" variant="ghost" className="size-7 shrink-0 text-sand-500" aria-label={t("backoffice:workspace.runAgent")}><Bot className="size-4" /></Button></DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
        {first.map(item)}
        {first.length && rest.length ? <DropdownMenuSeparator /> : null}
        {rest.map(item)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProgressRing({ value, total }: { value: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((value / total) * 100);
  const r = 22; const circ = 2 * Math.PI * r;
  return (
    <svg width={56} height={56} viewBox="0 0 56 56" className="shrink-0 -rotate-90" aria-hidden>
      <circle cx={28} cy={28} r={r} className="fill-none stroke-sand-200" strokeWidth={5} />
      <circle cx={28} cy={28} r={r} className={cn("fill-none transition-all", pct === 100 ? "stroke-success-500" : "stroke-aegold-600")} strokeWidth={5} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ - (circ * pct) / 100} />
      <text x={28} y={28} textAnchor="middle" dominantBaseline="central" className="rotate-90 fill-aeblack-900 text-[11px] font-semibold tabular" transform="rotate(90 28 28)">{pct}%</text>
    </svg>
  );
}
